import React from 'react';

import { tv } from '../../tv';

/**
 * Cover type for a generated document: the title page of a report, not a
 * heading in the product.
 *
 * `Heading` stops at 30px deliberately. Polaris caps its headings at exactly
 * that and Atlassian ships no larger tier at all, because dense product UI has
 * no use for bigger type and WCAG reflow makes it a liability on screen. The
 * systems that do go larger fence it off rather than extending their heading
 * scale: Primer keeps `Heading` at three sizes and puts display in a separate
 * token marked "use sparingly", and Material makes it a separate role.
 *
 * This follows that shape, and goes further because it can: the sizes are in
 * POINTS. A printed page is physical, so its type is measured in physical
 * units. CSS Values 4 says as much, and react-pdf drops `px` entirely for the
 * same reason. The renderer already emulates print media, so these resolve
 * against paper rather than a viewport that does not exist.
 *
 * Size and level are separate props on purpose. WCAG F2 is the failure where
 * appearance conveys a heading that markup does not, and a generated PDF
 * inherits its tags from this markup, so a cover title has to be a real
 * heading element whatever size it is rendered at.
 */

const displayStyles = tv({
  base: 'ck:font-semibold ck:text-text-default ck:text-balance',
  variants: {
    size: {
      /** A cover title. */
      lg: 'ck:text-print-lg',
      /** The organization the document belongs to. */
      md: 'ck:text-print-md',
      /** What the document is. */
      sm: 'ck:text-print-sm',
    },
    lines: {
      2: 'ck:line-clamp-2',
      4: 'ck:line-clamp-4',
      7: 'ck:line-clamp-7',
    },
    /* In points, like the type: these are intervals on a physical page. */
    spaceBefore: {
      sm: 'ck:mt-print-sm',
      md: 'ck:mt-print-md',
      lg: 'ck:mt-print-lg',
    },
  },
  defaultVariants: { size: 'lg' },
});

export interface DisplayProps {
  children?: React.ReactNode;
  /** Step on the cover scale. */
  size?: 'lg' | 'md' | 'sm';
  /**
   * Space above, on the cover's own rhythm. A cover sets its parts apart by
   * large, deliberate intervals rather than the product's spacing steps, and
   * the alternative is a wrapper per line whose only job is a margin.
   */
  spaceBefore?: 'sm' | 'md' | 'lg';
  /**
   * The heading level. Required: a cover title carries the document's
   * structure into the tagged PDF, and size never implies a level.
   */
  as: 'h1' | 'h2' | 'h3';
  /** Clamps a long title so it cannot push the cover past the page. */
  lines?: 2 | 4 | 7;
}

export const Display = React.forwardRef<HTMLHeadingElement, DisplayProps>(function Display(
  { children, size = 'lg', as: As, lines, spaceBefore },
  ref,
) {
  return (
    <As ref={ref} className={displayStyles({ size, lines, spaceBefore })} data-component="display">
      {children}
    </As>
  );
});
